/**
 * Bilingual Text Component (INT-CMP-009)
 * Renders text content with v3 bilingual support.
 * Resolves the correct language string based on user preferences.
 */

import React from 'react';
import type { TextStyle } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { FS_SM } from '../../theme';
import { useUserPreferencesStore } from '../../store/userPreferencesStore';
import { selectLessonLanguage, selectSourceLocale, selectTargetLocale } from '../../store/userPreferencesSelectors';
import { resolveBilingualContent, type BilingualContent } from '../../types/lesson';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';


interface BilingualTextProps {
  content: BilingualContent | string;
  style?: TextStyle;
  numberOfLines?: number;
  accessibilityRole?: 'header' | 'text' | 'none';
}

export const BilingualText = React.memo(function BilingualText({
  content,
  style,
  numberOfLines,
  accessibilityRole,
}: BilingualTextProps) {
  const { theme: t, fontScale, ffs } = useDisplayTheme();
  const lessonLanguage = useUserPreferencesStore(selectLessonLanguage);
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);

  const resolvedText = resolveBilingualContent(content, lessonLanguage, sourceLocale, targetLocale);

  // Simple markdown bold support: **text**
  const parts = resolvedText.split(/(\*\*[^*]+\*\*)/g);

  return (
    <Text
      allowFontScaling={false}
      numberOfLines={numberOfLines}
      accessibilityRole={accessibilityRole}
      style={{
        fontSize: FS_SM * fontScale,
        color: t.text,
        lineHeight: Math.ceil(20 * fontScale),
        ...ffs,
        ...style,
      }}
    >
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <Text key={i} allowFontScaling={false} style={{ fontWeight: 'bold', color: t.text, ...ffs }}>
              {part.slice(2, -2)}
            </Text>
          );
        }
        return part;
      })}
    </Text>
  );
});
